import type { GameEventController } from '../types';
import { openLuckyWheelPopup } from './behaviors/openLuckyWheelPopup';
import { LUCKY_WHEEL_MIN_LEVEL, luckyWheelRewards } from './LuckyWheelConstants';
import { computeRewardFromSpin, getNextSpinTime, isLuckyWheelAvailable } from './LuckyWheelUtils';
import type { LuckyWheelDependencies } from './types';
import { LuckyWheelMenuIconView } from './views/LuckyWheelMenuIconView';

export class LuckyWheelController implements GameEventController {
  public readonly featureName = 'Lucky Wheel';

  private toggleCoinCounterUpdateAction: ((value: boolean, checkForUpdate?: boolean) => void) | null = null;

  constructor(private readonly dependencies: LuckyWheelDependencies) {}

  public async startMainMenuFlow(): Promise<void> {
    const { observable } = this.dependencies;
    const { luckyWheel } = observable.getValue();

    if (isLuckyWheelAvailable(observable) && !luckyWheel.luckyWheelShown) {
      observable.updateValue({
        luckyWheel: {
          ...luckyWheel,
          luckyWheelShown: true,
        },
      });

      await openLuckyWheelPopup({
        dependencies: this.dependencies,
        toggleCoinCounterUpdate: (value: boolean, checkForUpdate?: boolean) => {
          this.toggleCoinCounterUpdateAction?.(value, checkForUpdate);
        },
      });
    }
  }

  public createGameEventIcon(): { icon: LuckyWheelMenuIconView; unlockLevel: number } {
    const { observable } = this.dependencies;
    const iconView = new LuckyWheelMenuIconView(observable);

    iconView.onClicked.connect(() => {
      void openLuckyWheelPopup({
        dependencies: this.dependencies,
        toggleCoinCounterUpdate: (value: boolean, checkForUpdate?: boolean) => {
          this.toggleCoinCounterUpdateAction?.(value, checkForUpdate);
        },
      });
    });
    return { icon: iconView, unlockLevel: LUCKY_WHEEL_MIN_LEVEL };
  }

  public toggleCoinCounterUpdate(action: (value: boolean, checkForUpdate?: boolean) => void): void {
    this.toggleCoinCounterUpdateAction = action;
  }

  public getDebugFunctions(): Record<string, () => void> {
    const { observable } = this.dependencies;

    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      'Generate test spin report': (): void => {
        const iterations = 10000;
        const rewardCounters: number[] = [];
        luckyWheelRewards.forEach((_, index) => {
          rewardCounters[index] = 0;
        });

        for (let i = 0; i < iterations; i += 1) {
          const rewardIndex = computeRewardFromSpin(observable, true);
          rewardCounters[rewardIndex] += 1;
        }

        console.log(`Lucky Wheel Test Spins with ${iterations} iterations`);
        rewardCounters.forEach((value, index) => {
          const wheelReward = luckyWheelRewards[index];
          if (wheelReward.isJackpot) {
            console.log(`Reward ${index} (Jackpot): ${value}`);
          } else {
            const rewardValue = wheelReward.reward
              ? `${wheelReward.reward.resource} ${wheelReward.reward.quantity}`
              : '';
            console.log(`Reward ${index} (${rewardValue}): ${value}`);
          }
        });
      },
      '+ 1 free spin': (): void => {
        const { luckyWheel } = observable.getValue();
        const freeSpins = luckyWheel.rewardedFreeSpins;
        const allowBonusFreeSpin = freeSpins === 0 && Date.now() > getNextSpinTime(luckyWheel.lastFreeSpinTime);
        observable.updateValue({
          luckyWheel: {
            ...luckyWheel,
            rewardedFreeSpins: freeSpins + 1,
            bonusFreeSpinTime: allowBonusFreeSpin ? Date.now() : 0,
          },
        });
        console.log(observable.getValue().luckyWheel.rewardedFreeSpins);
      },
      'Reset all spin data': (): void => {
        const { luckyWheel } = observable.getValue();
        observable.updateValue({
          luckyWheel: {
            ...luckyWheel,
            lastFreeSpinTime: 0,
            lastAdSpinTime: 0,
            rewardedFreeSpins: 0,
            adSpinCount: 0,
            lastWonRewardIndex: null,
          },
        });
      },
      'Use up all spins': (): void => {
        const { luckyWheel } = observable.getValue();
        observable.updateValue({
          luckyWheel: {
            ...luckyWheel,
            lastFreeSpinTime: Date.now(),
            lastAdSpinTime: Date.now(),
            rewardedFreeSpins: 0,
            adSpinCount: 0,
            lastWonRewardIndex: 0,
          },
        });
      },
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }
}
